import { useDispatch, useSelector } from "react-redux";
import {
  selectAvailableIconAssets,
  selectIsDarkMode,
  selectUnavailableIconLinks,
} from "@shared/slices/common.slice.ts";
import { EXCHANGES, ICON_TYPES } from "@shared/common/constants.ts";
import { IconType } from "@shared/types";
import { sharedLib } from "@shared/services/shared.service.ts";
import { useMemo } from "react";
import {
  getAssetIcon,
  getExchangeIcon,
  getIconAssetName,
} from "common/Icons/utils.ts";

export interface useIconWithFallbackProps {
  coinOrExchangeName?: string;
  iconType?: IconType;
  iconLink?: string;
  removeOnError?: boolean;
}

export const useIconWithFallback = ({
  coinOrExchangeName,
  iconType,
  iconLink,
  removeOnError = true,
}: useIconWithFallbackProps) => {
  const isDarkMode = useSelector(selectIsDarkMode);
  const assetName = getIconAssetName(
    coinOrExchangeName?.split("/")[0].toLowerCase() || ""
  );
  const dispatch = useDispatch();
  const availableIconAssets = useSelector(selectAvailableIconAssets);
  const unavailableIconLinks = useSelector(selectUnavailableIconLinks);

  const checkError = (name?: string, iconLink?: string) => {
    // A given link is what gets loaded (see iconUrl below), so its failure
    // decides the fallback even when a name is passed too (e.g. groups).
    if (iconLink) {
      return unavailableIconLinks.has(iconLink);
    } else if (name) {
      return (
        iconType === ICON_TYPES.COIN &&
        availableIconAssets.size &&
        !availableIconAssets.has(name)
      );
    }
  };
  const error = checkError(assetName, iconLink);

  const imageFilters = useMemo(() => {
    if (
      isDarkMode &&
      iconType === ICON_TYPES.EXCHANGE &&
      coinOrExchangeName?.includes(EXCHANGES.OKX)
    ) {
      return { filter: "invert(100%)" };
    }
  }, [isDarkMode, iconType, coinOrExchangeName]);

  if (!coinOrExchangeName && !iconLink) return {};

  const getFallbackLetter = () => {
    if (coinOrExchangeName) return coinOrExchangeName?.charAt(0);
    else if (iconLink) {
      return iconLink.split("/").pop()?.charAt(0);
    }
  };

  let iconUrl: string | undefined;
  if (iconLink) {
    iconUrl = iconLink;
  } else if (coinOrExchangeName && iconType === ICON_TYPES.EXCHANGE) {
    iconUrl = getExchangeIcon(coinOrExchangeName);
  } else if (iconType === ICON_TYPES.COIN) {
    iconUrl = getAssetIcon(assetName);
  }

  const handleError = () => {
    if (removeOnError) {
      dispatch(
        sharedLib.slices.common.actions.removeFromAvailableIconAssets(assetName)
      );
    }
    dispatch(
      sharedLib.slices.common.actions.addToUnavailableIconLinks(iconLink)
    );
  };

  return {
    fallbackLetter: getFallbackLetter(),
    iconUrl,
    handleError,
    error,
    imageFilters,
  };
};
